import React, { createContext, useContext, useState, useCallback } from 'react';

const WidgetStateContext = createContext(null);

/**
 * Hook to read and write state for a specific widget ID.
 * This is REACTIVE. If multiple components use the same uniqueId, they will sync.
 * 
 * @param {string} uniqueId - Unique identifier for the state slice.
 * @param {any} initialState - Default state if nothing is cached.
 * @returns {[any, Function]} - [currentState, updateState]
 */
export const useWidgetState = (uniqueId, initialState) => {
    const context = useContext(WidgetStateContext);
    if (!context) {
        throw new Error('useWidgetState must be used within a WidgetStateProvider');
    }
    const { widgetStates, setWidgetState } = context;

    // Read directly from the central store to ensure reactivity across components sharing the same ID
    const currentValue = widgetStates.hasOwnProperty(uniqueId) 
        ? widgetStates[uniqueId] 
        : initialState;

    const updateState = useCallback((newValue) => {
        setWidgetState(uniqueId, (prevValue) => {
             // Handle functional updates based on the *store's* previous value for this ID
             const actualPrev = prevValue !== undefined ? prevValue : initialState;
             return typeof newValue === 'function' ? newValue(actualPrev) : newValue;
        });
    }, [uniqueId, setWidgetState, initialState]);

    return [currentValue, updateState];
};

/**
 * Hook to access administrative actions for the widget store (e.g. clearing).
 */
export const useWidgetStateActions = () => {
    const context = useContext(WidgetStateContext);
    if (!context) {
        throw new Error('useWidgetStateActions must be used within a WidgetStateProvider');
    }
    return {
        clearAllStates: context.clearAllStates
    };
};

export const WidgetStateProvider = ({ children }) => {
    const [widgetStates, setWidgetStates] = useState({});

    // This function handles the logic of updating the big state object
    const setWidgetStateWrapper = useCallback((id, valueOrFn) => {
        setWidgetStates(prev => {
            const oldValue = prev[id];
            // Resolve the new value. If valueOrFn is a function, pass the *specific* old value
            const newValue = typeof valueOrFn === 'function' 
                ? valueOrFn(oldValue) 
                : valueOrFn;
            
            if (oldValue === newValue) return prev; // No change optimization
            
            return {
                ...prev,
                [id]: newValue
            };
        });
    }, []);

    const clearAllStates = useCallback(() => {
        setWidgetStates({});
    }, []);

    const value = {
        widgetStates, // Pass the whole object so hooks verify changes
        setWidgetState: setWidgetStateWrapper,
        clearAllStates
    };

    return (
        <WidgetStateContext.Provider value={value}>
            {children}
        </WidgetStateContext.Provider>
    );
};